import React, { useState, useEffect, useCallback, useRef } from 'react';
import { FaPaperPlane, FaPaperclip, FaSmile, FaSpinner } from 'react-icons/fa';
import AxiosInstance from '../../../../axiosConfig';
import DialogBox from './designer_components/Dialog';

const ChatInterface = () => {
  // State for chats, messages, and dialog
  const [chats, setChats] = useState([]);
  const [messages, setMessages] = useState([]);
  const [dialog, setDialog] = useState({
    isOpen: false,
    title: '',
    message: '',
    status: 'info',
    onConfirm: null,
  });
  const [selectedChat, setSelectedChat] = useState(null);
  const [newMessage, setNewMessage] = useState('');
  const [showEndChatDropdown, setShowEndChatDropdown] = useState(false);
  const [showEscalateDialog, setShowEscalateDialog] = useState(false);
  const [escalateForm, setEscalateForm] = useState({
    task_name: '',
    priority: '',
    case_type: '',
    notes: '',
    assignTo: '',
  });

  // New state for loading more messages
  const [isLoadingMore, setIsLoadingMore] = useState(false);
  const [hasMoreMessages, setHasMoreMessages] = useState(true);

  // Ref for messages container to manage scroll position
  const messagesContainerRef = useRef(null);

  // Ref to store timeout ID for dialog auto-dismiss
  const dialogTimeoutRef = useRef(null);

  // Static data for dropdowns
  const endChatReasons = ['Query Resolved', 'Customer Irresponsive', 'Escalate to a Case'];
  const priorityLevels = ['Low', 'Medium', 'High', 'Critical'];
  const [caseTypes, setCaseTypes] = useState([]);
  const [assignees, setAssignees] = useState([]);

  // Format date string to readable format
  const formatDate = (dateString) => {
    const date = new Date(dateString);
    return isNaN(date.getTime()) 
      ? 'Invalid date' 
      : date.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
  };

  // Format full date for chat display
  const formatFullDate = (dateString) => {
    const date = new Date(dateString);
    return isNaN(date.getTime())
      ? 'Invalid date'
      : date.toLocaleString([], {
          month: 'short',
          day: 'numeric',
          year: 'numeric',
          hour: '2-digit',
          minute: '2-digit',
        });
  };

  // Handle chat selection - COMPLETELY CLEAR ALL MESSAGES
  const handleChatSelect = useCallback((chat) => {
    // Completely reset messages and loading state
    setMessages([]);
    setHasMoreMessages(true);
    setIsLoadingMore(false);

    setSelectedChat(chat);
    setChats((prevChats) =>
      prevChats.map((c) => (c.id === chat.id ? { ...c, unread: false } : c))
    );

    // After state is cleared, load new messages
    setTimeout(() => {
      const threadMessages = Object.entries(chat.threadData).map(([threadId, thread]) => {
        return [
          {
            id: `${chat.sender_id}-${threadId}-user`,
            sender_id: chat.sender_id,
            text: thread.content.user_question,
            timestamp: formatFullDate(thread.in_chat_time),
            isUser: true,
            threadId: parseInt(threadId),
          },
          {
            id: `${chat.sender_id}-${threadId}-bot`,
            sender_id: 'bot',
            text: thread.content.bot_response,
            timestamp: formatFullDate(thread.out_chat_time),
            isUser: false,
            threadId: parseInt(threadId),
          }
        ];
      }).flat();

      setMessages(threadMessages);
    }, 0);
  }, [setMessages, setHasMoreMessages, setIsLoadingMore, setSelectedChat, setChats]);

  // Fetch escalated chats on component mount
  useEffect(() => {
    const fetchEscalatedChats = async () => {
      try {
        const response = await AxiosInstance.get('/get-escalated-chats');
        const { all_chats, available_agents, case_types } = response.data.message;

        const mappedChats = all_chats.map((chat, index) => {
          const threadId = Object.keys(chat.thread)[0];
          const thread = chat.thread[threadId];
          
          return {
            id: index + 1,
            sender_id: chat.sender_id,
            name: chat.sender_name,
            channel: thread.content.channel_name,
            lastMessage: thread.content.user_question,
            timestamp: formatDate(thread.in_chat_time),
            fullTimestamp: formatFullDate(thread.in_chat_time),
            unread: true,
            case_type: chat.case_type,
            online_status: chat.online_status,
            threadData: chat.thread,
          };
        });

        setChats(mappedChats);

        // Define and call a local chat handler function to avoid dependency issues
        const selectInitialChat = (chat) => {
          setMessages([]);
          setHasMoreMessages(true);
          setIsLoadingMore(false);
          setSelectedChat(chat);
          setChats((prevChats) =>
            prevChats.map((c) => (c.id === chat.id ? { ...c, unread: false } : c))
          );

          setTimeout(() => {
            const threadMessages = Object.entries(chat.threadData).map(([threadId, thread]) => {
              return [
                {
                  id: `${chat.sender_id}-${threadId}-user`,
                  sender_id: chat.sender_id,
                  text: thread.content.user_question,
                  timestamp: formatFullDate(thread.in_chat_time),
                  isUser: true,
                  threadId: parseInt(threadId),
                },
                {
                  id: `${chat.sender_id}-${threadId}-bot`,
                  sender_id: 'bot',
                  text: thread.content.bot_response,
                  timestamp: formatFullDate(thread.out_chat_time),
                  isUser: false,
                  threadId: parseInt(threadId),
                }
              ];
            }).flat();

            setMessages(threadMessages);
          }, 0);
        };

        if (mappedChats.length > 0) {
          selectInitialChat(mappedChats[0]);
        }

        const mappedAssignees = Object.entries(available_agents)
          .filter(([_, name]) => name)
          .map(([id, name]) => ({ id, name }));

        setAssignees(mappedAssignees);
        setCaseTypes(case_types);
      } catch (err) {
        showDialog(
          'Error',
          err.response?.data?.message || err.message || 'Failed to fetch escalated chats',
          'error'
        );
      }
    };

    fetchEscalatedChats();
  }, []);

  // Dialog helper function with auto-dismiss option
  const showDialog = (title, message, status = 'info', onConfirm = null, autoDismiss = false) => {
    // Clear any existing timeout
    if (dialogTimeoutRef.current) {
      clearTimeout(dialogTimeoutRef.current);
    }

    // Set dialog state
    setDialog({
      isOpen: true,
      title,
      message,
      status,
      onConfirm,
    });

    // Set auto-dismiss timeout if specified
    if (autoDismiss) {
      dialogTimeoutRef.current = setTimeout(() => {
        setDialog((prev) => ({ ...prev, isOpen: false }));
        dialogTimeoutRef.current = null;
      }, 500); // Auto-dismiss after 0.5 seconds
    }
  };

  // Cleanup timeout on component unmount
  useEffect(() => {
    return () => {
      if (dialogTimeoutRef.current) {
        clearTimeout(dialogTimeoutRef.current);
      }
    };
  }, []);

  // Function to load more messages
  const loadMoreMessages = async () => {
    if (!selectedChat || isLoadingMore || !hasMoreMessages) return;

    setIsLoadingMore(true);

    // Capture the current scroll height and position
    const messagesContainer = messagesContainerRef.current;
    const previousScrollHeight = messagesContainer ? messagesContainer.scrollHeight : 0;

    try {
      // Get the MINIMUM (oldest) message thread ID to fetch older messages
      const minThreadId = messages.length > 0
        ? messages.reduce((min, msg) => Math.min(min, msg.threadId), Infinity)
        : 0;

      const response = await AxiosInstance.post('/load-sender-messages', {
        from_sender: selectedChat.sender_id,
        channel_name: selectedChat.channel,
        last_message_id: minThreadId
      });

      if (response.data.status === 'success') {
        const newMessages = Object.entries(response.data.message).map(([threadId, thread]) => {
          return [
            {
              id: `${selectedChat.sender_id}-${threadId}-user`,
              sender_id: selectedChat.sender_id,
              text: thread.content.user_question,
              timestamp: formatFullDate(thread.in_chat_time),
              isUser: true,
              threadId: parseInt(threadId),
            },
            {
              id: `${selectedChat.sender_id}-${threadId}-bot`,
              sender_id: 'bot',
              text: thread.content.bot_response,
              timestamp: formatFullDate(thread.out_chat_time),
              isUser: false,
              threadId: parseInt(threadId),
            }
          ];
        }).flat();

        if (newMessages.length === 0) {
          setHasMoreMessages(false);
        } else {
          // Prepend new messages to the start of the array
          setMessages(prev => [...newMessages, ...prev]);

          // Adjust scroll position to maintain view
          setTimeout(() => {
            if (messagesContainer) {
              const newScrollHeight = messagesContainer.scrollHeight;
              messagesContainer.scrollTop = newScrollHeight - previousScrollHeight;
            }
          }, 0);
        }
      }
    } catch (err) {
      showDialog(
        'Error',
        err.response?.data?.message || err.message || 'Failed to load more messages',
        'error'
      );
    } finally {
      setIsLoadingMore(false);
    }
  };

  // Handle sending a new message
  const handleSendMessage = async (e) => {
    e.preventDefault();
    if (!newMessage.trim() || !selectedChat) return;

    try {
      const response = await AxiosInstance.post('/agent-responds', {
        sender_id: selectedChat.sender_id,
        channel: selectedChat.channel,
        message: newMessage,
      });

      if (response.data.status === 'success') {
        // Show success dialog with auto-dismiss
        showDialog('Success', response.data.message || 'Message sent!', 'success', null, true);

        // Append the new message to the UI
        const newMsg = {
          id: `agent-${Date.now()}`,
          sender_id: 'agent',
          text: newMessage,
          timestamp: formatFullDate(new Date()),
          isUser: false,
        };

        setMessages((prev) => [...prev, newMsg]);
        setNewMessage('');
        setChats((prevChats) =>
          prevChats.map((c) =>
            c.id === selectedChat.id ? { 
              ...c, 
              lastMessage: newMessage, 
              timestamp: formatDate(new Date()),
              fullTimestamp: formatFullDate(new Date())
            } : c
          )
        );
      } else {
        // Show error dialog with auto-dismiss
        showDialog('Error', response.data.message || 'Failed to send message', 'error', null, true);
      }
    } catch (err) {
      // Show error dialog with auto-dismiss
      showDialog(
        'Error',
        err.response?.data?.message || err.message || 'Failed to send message',
        'error',
        null,
        true
      );
    }
  };

  // Handle ending a chat
  const handleEndChat = (reason) => {
    setShowEndChatDropdown(false);
    if (reason === 'Escalate to a Case') {
      setShowEscalateDialog(true);
    } else {
      console.log(`Chat ended with reason: ${reason}`);
      setChats((prevChats) => prevChats.filter((c) => c.id !== selectedChat.id));
      setSelectedChat(chats.length > 1 ? chats[1] : null);
      setMessages([]);
    }
  };

  // Handle case escalation submission
  const handleEscalateSubmit = async (e) => {
    e.preventDefault();
    const payload = {
      task_name: escalateForm.task_name,
      description: escalateForm.notes,
      priority: escalateForm.priority,
      sender_name: selectedChat.name,
      sender_id: selectedChat.sender_id,
      channel_name: selectedChat.channel,
      case_type: escalateForm.case_type,
    };

    try {
      const response = await AxiosInstance.post('/create-a-case', payload);
      showDialog(
        'Success',
        response?.data?.message || 'Case created successfully!',
        'success',
        () => {
          setShowEscalateDialog(false);
          setEscalateForm({ task_name: '', priority: '', case_type: '', notes: '', assignTo: '' });
          setChats((prevChats) => prevChats.filter((c) => c.id !== selectedChat.id));
          setSelectedChat(chats.length > 1 ? chats[1] : null);
          setMessages([]);
        }
      );
    } catch (err) {
      showDialog(
        'Error',
        err.response?.data?.message || err.message || 'Failed to create a case',
        'error'
      );
    }
  };

  return (
    <div className="bg-gray-50 min-h-screen flex">
      {/* Sidebar: Chat List */}
      <div className="w-1/4 bg-white border-r border-gray-200 flex flex-col" style={{ height: 'calc(100vh - 3rem)' }}>
        <div className="p-4 border-b border-gray-200">
          <h2 className="text-lg font-bold text-gray-800">Escalated Chats</h2>
          <p className="text-sm text-gray-500">{chats.length} active chats</p>
        </div>
        <div className="flex-1 overflow-y-auto" style={{ maxHeight: 'calc(100vh - 9rem)' }}>
          {chats.length === 0 ? (
            <div className="p-4 text-center text-gray-500">
              No active chats available
            </div>
          ) : (
            chats.map((chat) => (
              <div
                key={chat.id}
                onClick={() => handleChatSelect(chat)}
                className={`p-4 border-b border-gray-100 cursor-pointer hover:bg-gray-50 ${
                  selectedChat?.id === chat.id ? 'bg-blue-50' : ''
                }`}
              >
                <div className="flex justify-between items-start">
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center justify-between">
                      <h3 className={`text-sm font-medium truncate ${chat.unread ? 'text-gray-900 font-bold' : 'text-gray-700'}`}>
                        {chat.name}
                      </h3>
                      <span className="text-xs text-gray-500 whitespace-nowrap ml-2">{chat.timestamp}</span>
                    </div>
                    <p className={`text-sm ${chat.unread ? 'text-gray-900 font-medium' : 'text-gray-500'} truncate`}>
                      {chat.lastMessage}
                    </p>
                    <div className="flex justify-between items-center mt-1">
                      <span className="text-xs text-gray-400 capitalize truncate">{chat.channel}</span>
                      <span className={`text-xs px-2 py-1 rounded whitespace-nowrap ${
                        chat.case_type === 'general' 
                          ? 'bg-green-100 text-green-800' 
                          : 'bg-blue-100 text-blue-800'
                      }`}>
                        {chat.case_type}
                      </span>
                    </div>
                  </div>
                  {chat.unread && (
                    <span className="h-2 w-2 bg-blue-600 rounded-full mt-2 ml-2 flex-shrink-0"></span>
                  )}
                </div>
              </div>
            ))
          )}
        </div>
      </div>

      {/* Main Chat Area */}
      <div className="w-3/4 flex flex-col" style={{ height: 'calc(100vh - 3rem)' }}>
        {/* Chat Header */}
        {selectedChat ? (
          <>
            <div className="p-4 bg-white border-b border-gray-200">
              <div className="flex items-center justify-between">
                <div className="min-w-0">
                  <h2 className="text-lg font-bold text-gray-800 truncate">{selectedChat.name}</h2>
                  <div className="flex space-x-4 text-sm text-gray-500 mt-1">
                    <span className="truncate">Channel: <span className="capitalize">{selectedChat.channel}</span></span>
                    <span className="truncate">Sender ID: {selectedChat.sender_id}</span>
                    <span className={`flex items-center whitespace-nowrap ${
                      selectedChat.online_status ? 'text-green-500' : 'text-gray-500'
                    }`}>
                      <span className={`h-2 w-2 rounded-full mr-1 ${
                        selectedChat.online_status ? 'bg-green-500' : 'bg-gray-500'
                      }`}></span>
                      {selectedChat.online_status ? 'Online' : 'Offline'}
                    </span>
                  </div>
                </div>
                <div className="relative flex-shrink-0">
                  <button
                    onClick={() => setShowEndChatDropdown(!showEndChatDropdown)}
                    className="px-4 py-2 bg-red-500 text-white rounded-md hover:bg-red-600 whitespace-nowrap"
                  >
                    End Chat
                  </button>
                  {showEndChatDropdown && (
                    <div className="absolute right-0 mt-2 w-48 bg-white border border-gray-200 rounded-md shadow-lg z-10">
                      {endChatReasons.map((reason) => (
                        <button
                          key={reason}
                          onClick={() => handleEndChat(reason)}
                          className="block w-full text-left px-4 py-2 text-sm text-gray-700 hover:bg-gray-100 whitespace-nowrap"
                        >
                          {reason}
                        </button>
                      ))}
                    </div>
                  )}
                </div>
              </div>
            </div>

            {/* Messages Area */}
            <div
              className="flex-1 p-6 bg-gray-100 overflow-y-auto"
              style={{ maxHeight: 'calc(100vh - 12rem)' }}
              ref={messagesContainerRef}
            >
              {messages.length === 0 ? (
                <div className="text-center text-gray-500 py-8">
                  No messages in this chat
                </div>
              ) : (
                <>
                  <div className="flex justify-center mb-4">
                    <button
                      onClick={loadMoreMessages}
                      disabled={isLoadingMore || !hasMoreMessages}
                      className={`px-4 py-2 rounded-md text-sm ${
                        isLoadingMore || !hasMoreMessages
                          ? 'bg-gray-200 text-gray-500 cursor-not-allowed'
                          : 'bg-blue-100 text-blue-600 hover:bg-blue-200'
                      } flex items-center`}
                    >
                      {isLoadingMore ? (
                        <>
                          <FaSpinner className="animate-spin mr-2" />
                          Loading...
                        </>
                      ) : hasMoreMessages ? (
                        'Load Older Messages'
                      ) : (
                        'No More Messages'
                      )}
                    </button>
                  </div>

                  {messages.map((message) => (
                    <div
                      key={message.id}
                      className={`mb-4 flex ${message.isUser ? 'justify-start' : 'justify-end'}`}
                    >
                      <div
                        className={`max-w-[70%] p-3 rounded-lg ${
                          message.isUser 
                            ? 'bg-white border border-gray-200' 
                            : 'bg-blue-600 text-white'
                        }`}
                      >
                        <p className="text-sm break-words">{message.text}</p>
                        <div className="flex justify-between items-center mt-1">
                          <span className={`text-xs ${
                            message.isUser ? 'text-gray-500' : 'text-blue-100'
                          }`}>
                            {message.timestamp}
                          </span>
                          {!message.isUser && (
                            <span className="text-xs text-blue-100 ml-2">
                              Replied by: {message.sender_id === 'bot' ? 'TheBOT' : 'Agent'}
                            </span>
                          )}
                        </div>
                      </div>
                    </div>
                  ))}
                </>
              )}
            </div>

            {/* Message Input - Fixed at bottom */}
            <div className="p-4 bg-white border-t border-gray-200">
              <form onSubmit={handleSendMessage} className="flex items-center">
                <textarea
                  value={newMessage}
                  onChange={(e) => setNewMessage(e.target.value)}
                  placeholder="Type your message..."
                  className="flex-1 p-3 border border-gray-300 rounded-md resize-none h-12 focus:outline-none focus:ring-2 focus:ring-blue-600"
                />
                <div className="flex items-center space-x-2 ml-2">
                  <button type="button" className="text-gray-500 hover:text-gray-700">
                    <FaPaperclip />
                  </button>
                  <button type="button" className="text-gray-500 hover:text-gray-700">
                    <FaSmile />
                  </button>
                  <button
                    type="submit"
                    disabled={!newMessage.trim()}
                    className="flex items-center px-6 py-2 rounded-md bg-blue-600 text-white hover:bg-blue-700 disabled:bg-blue-400 whitespace-nowrap"
                  >
                    Send <FaPaperPlane className="ml-2" />
                  </button>
                </div>
              </form>
            </div>
          </>
        ) : (
          <div className="flex-1 flex items-center justify-center bg-gray-100">
            <div className="text-center p-6">
              <h3 className="text-lg font-medium text-gray-700 mb-2">No chat selected</h3>
              <p className="text-gray-500">
                {chats.length === 0 
                  ? 'No chats available' 
                  : 'Select a chat from the sidebar to view messages'}
              </p>
            </div>
          </div>
        )}
      </div>

      {/* Escalate to Case Dialog */}
      {showEscalateDialog && selectedChat && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
          <div className="bg-white rounded-lg shadow-xl max-w-md w-full max-h-[80vh] overflow-y-auto">
            <div className="p-6">
              <h3 className="text-lg font-medium text-gray-900 mb-4">Create New Case</h3>
              <form onSubmit={handleEscalateSubmit}>
                <div className="grid grid-cols-1 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700">Customer Name</label>
                    <input
                      type="text"
                      value={selectedChat.name}
                      disabled
                      className="w-full p-2 border border-gray-300 rounded-md bg-gray-100"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700">Channel</label>
                    <input
                      type="text"
                      value={selectedChat.channel}
                      disabled
                      className="w-full p-2 border border-gray-300 rounded-md bg-gray-100 capitalize"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700">Sender ID</label>
                    <input
                      type="text"
                      value={selectedChat.sender_id}
                      disabled
                      className="w-full p-2 border border-gray-300 rounded-md bg-gray-100"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700">Task Name</label>
                    <input
                      type="text"
                      value={escalateForm.task_name}
                      onChange={(e) => setEscalateForm({ ...escalateForm, task_name: e.target.value })}
                      className="w-full p-2 border border-gray-300 rounded-md"
                      required
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700">Case Type</label>
                    <select
                      value={escalateForm.case_type}
                      onChange={(e) => setEscalateForm({ ...escalateForm, case_type: e.target.value })}
                      className="w-full p-2 border border-gray-300 rounded-md"
                      required
                    >
                      <option value="">Select Case Type</option>
                      {caseTypes.map((type) => (
                        <option key={type} value={type}>
                          {type}
                        </option>
                      ))}
                    </select>
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700">Priority</label>
                    <select
                      value={escalateForm.priority}
                      onChange={(e) => setEscalateForm({ ...escalateForm, priority: e.target.value })}
                      className="w-full p-2 border border-gray-300 rounded-md"
                      required
                    >
                      <option value="">Select Priority</option>
                      {priorityLevels.map((level) => (
                        <option key={level} value={level}>
                          {level}
                        </option>
                      ))}
                    </select>
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700">Assign To</label>
                    <select
                      value={escalateForm.assignTo}
                      onChange={(e) => setEscalateForm({ ...escalateForm, assignTo: e.target.value })}
                      className="w-full p-2 border border-gray-300 rounded-md"
                    >
                      <option value="">Select Assignee</option>
                      {assignees.map((assignee) => (
                        <option key={assignee.id} value={assignee.id}>
                          {assignee.name}
                        </option>
                      ))}
                    </select>
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700">Case Notes</label>
                    <textarea
                      value={escalateForm.notes}
                      onChange={(e) => setEscalateForm({ ...escalateForm, notes: e.target.value })}
                      className="w-full p-2 border border-gray-300 rounded-md h-24"
                      required
                    />
                  </div>
                </div>
                <div className="mt-6 flex justify-end space-x-2">
                  <button
                    type="button"
                    onClick={() => setShowEscalateDialog(false)}
                    className="px-4 py-2 bg-gray-200 text-gray-700 rounded-md hover:bg-gray-300"
                  >
                    Cancel
                  </button>
                  <button
                    type="submit"
                    disabled={
                      !escalateForm.task_name ||
                      !escalateForm.case_type ||
                      !escalateForm.priority ||
                      !escalateForm.notes
                    }
                    className="px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 disabled:bg-blue-400"
                  >
                    Submit Case
                  </button>
                </div>
              </form>
            </div>
          </div>
        </div>
      )}

      {/* Dialog Box */}
      <DialogBox
        isOpen={dialog.isOpen}
        title={dialog.title}
        message={dialog.message}
        status={dialog.status}
        onClose={() => setDialog({ ...dialog, isOpen: false })}
        onConfirm={dialog.onConfirm}
      />
    </div>
  );
};

export default ChatInterface;